import { Check, X } from "lucide-react"
import { Button } from "../../../components/button"
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal"
import RejectPermitModal from "../../../components/modal/RejectPermitModal"
import { DescriptionOfWarehouseItem, PERMIT_TYPES, STATUS_IDS, USER_ROLES_IDS, Vehicle, WarehousePermitWorker } from "../../../constants/common"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import useAuthStore from "../../../store/Auth"
import { useCallback, useMemo, useState } from "react"
import { APP_ROUTES } from "../../../routes"
import toast from "react-hot-toast"
import { fileToBase64, formattedDateTime } from "../../../lib/functions"
import { useAddUpdateWarehouseInOutPermit } from "../../../hooks/permit/mutation"
import { UseFormHandleSubmit, UseFormSetError } from "react-hook-form"

interface FormData {
    exitFromBuilding?: string | null;
    entryIntoBuilding?: string | null;
    uploadEscortingEmployeeIDCard?: any;
    escortEmployee: string;
    permitStartDate: string;
    permitEndDate: string;
    generalDescriptionOther?: string;
}

interface ActionButtonGroupProps {
    warehousePermit?: {
        n_PermitWarehouseID: number;
        n_ApprovalStatus: number;
        n_PermitNextApproverRoleID: number
        permitEndDate: string;
    }
    handleSubmit: any
    setError: UseFormSetError<{
        entryIntoBuilding?: string | undefined;
        exitFromBuilding?: string | undefined;
        uploadEscortingEmployeeIDCard: any;
        generalDescriptionOther?: string | undefined;
        escortEmployee: string;
        permitStartDate: string;
        permitEndDate: string;
    }>
    generalDescription: number
    addedWorkers: WarehousePermitWorker[]
    addedDescriptionOfWarehouseItems: DescriptionOfWarehouseItem[]
    addedVehicles: Vehicle[];
    isGeneralDescriptionOther: boolean;
    isPermitApprovedByNextApprover: boolean;
    isFieldsDisabled: boolean
}

const ActionButtonGroup = ({
    warehousePermit,
    handleSubmit,
    setError,
    generalDescription,
    addedWorkers,
    addedDescriptionOfWarehouseItems,
    addedVehicles,
    isGeneralDescriptionOther,
    isPermitApprovedByNextApprover,
    isFieldsDisabled
}: ActionButtonGroupProps) => {
    const { t } = useTranslation()

    const location = useLocation();

    const { user } = useAuthStore()

    const navigate = useNavigate()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const {
        mutateAsync: AddUpdateWarehouseInOutPermit,
        isPending: isAddUpdateWarehouseInOutPermitLoading,
    } = useAddUpdateWarehouseInOutPermit();

    const onSubmit = useCallback(
        async (data: FormData) => {
            if (!data.entryIntoBuilding && !data.exitFromBuilding) {
                toast.error(t("entryIntoExitFromBuildingIsRequired"));

                setError("entryIntoBuilding", {
                    type: "manual",
                    message: t("entryExitFromBuildingIsRequired"),
                });
                setError("exitFromBuilding", {
                    type: "manual",
                    message: t("entryExitFromBuildingIsRequired"),
                });
                return;
            } else if (!generalDescription) {
                toast.error(t("selectAtLeastOneGeneralDescription"));
            } else if (addedWorkers.length === 0) {
                toast.error(t("addAnAccompanyingPersonnel"));
            } else if (addedDescriptionOfWarehouseItems.length === 0) {
                toast.error(t("addDetailedDescriptionOfItems"));
            } else if (addedVehicles.length === 0) {
                toast.error(t("addAVehicle"));
            } else {
                const body = {
                    n_PermitWarehouseID: key ? Number(key) : 0,
                    s_ExitBuilding: data.exitFromBuilding,
                    s_EntryBuilding: data.entryIntoBuilding,

                    n_UserID: Number(data.escortEmployee),
                    s_UserCardBase64: data?.uploadEscortingEmployeeIDCard
                        ? await fileToBase64(data.uploadEscortingEmployeeIDCard)
                        : "",
                    s_UserCardContentType:
                        data.uploadEscortingEmployeeIDCard &&
                        data.uploadEscortingEmployeeIDCard.type,

                    s_PermitDescriptionCode: generalDescription.toString(),
                    s_PermitDescriptionOthersValue: isGeneralDescriptionOther
                        ? data.generalDescriptionOther
                        : "",
                    dt_PermitStartDate: formattedDateTime(new Date(data.permitStartDate)),
                    dt_PermitEndDate: formattedDateTime(new Date(data.permitEndDate)),

                    permitPersonnel: await Promise.all(
                        addedWorkers.map(async (worker) => {
                            const isAttachedVehicleToAWorker = addedVehicles.find(
                                (vehicle) =>
                                    vehicle.driversId === worker.id ||
                                    vehicle.personnelId === Number(worker.id)
                            );

                            return {
                                n_PermitPersonnelID:
                                    worker.id && typeof worker.id === "number" ? worker.id : 0,
                                n_PermitWarehouseID: key ? Number(key) : 0,
                                s_TempClientID: worker.id,
                                s_PersonnelName: worker.name,
                                nationality: worker.nationality,
                                s_Iqama: worker.residencePermitNo,
                                dt_IqamaIssue: formattedDateTime(new Date(worker.issueDate)),
                                s_IssuingAuthority: worker.issuingAuthority,

                                s_IqamaUploadBase64:
                                    worker.uploadId && typeof worker.uploadId !== "boolean"
                                        ? await fileToBase64(worker.uploadId)
                                        : "",
                                s_IqamaContentType: worker.uploadId ? worker.uploadId.type : "",
                                haveIqamaUpload:
                                    worker.uploadId && typeof worker.uploadId === "boolean"
                                        ? 1
                                        : 0,

                                n_IsPersonnelDriver: isAttachedVehicleToAWorker ? 1 : 0,

                                s_VehicleRegistrationFileBase64:
                                    isAttachedVehicleToAWorker?.uploadVehicleRegLicense &&
                                        typeof isAttachedVehicleToAWorker?.uploadVehicleRegLicense !==
                                        "boolean"
                                        ? await fileToBase64(
                                            isAttachedVehicleToAWorker.uploadVehicleRegLicense
                                        )
                                        : "",
                                s_VehicleRegistrationContentType: isAttachedVehicleToAWorker
                                    ? isAttachedVehicleToAWorker.uploadVehicleRegLicense?.type
                                    : "",
                                haveVehicleRegistrationFile:
                                    isAttachedVehicleToAWorker?.uploadVehicleRegLicense &&
                                        typeof isAttachedVehicleToAWorker?.uploadVehicleRegLicense ===
                                        "boolean"
                                        ? 1
                                        : 0,
                            };
                        })
                    ),

                    permitItems: addedDescriptionOfWarehouseItems.map((item) => ({
                        n_PermitItemID:
                            item.id && typeof item.id === "number" ? Number(item.id) : 0,
                        s_ItemType: item.itemType,
                        n_Quantity: Number(item.quantity),
                        s_SupplyingOrExportingEntity:
                            item.supplyingExportingEntityForPermitHolder,
                    })),

                    permitVehicles: addedVehicles.map((vehicle) => {
                        const isPersonnel = addedWorkers.find(
                            (worker) => Number(worker.id) === Number(vehicle.personnelId)
                        );

                        return {
                            n_PermitVehicleID:
                                vehicle.id && typeof vehicle.id === "number"
                                    ? Number(vehicle.id)
                                    : 0,
                            n_PermitWarehouseID: key ? Number(key) : 0,
                            n_DriverPersonnelID: vehicle.personnelId || 0,
                            s_DriverTempClientID: isPersonnel ? "" : vehicle.driversId,
                            n_PlateSource: Number(vehicle.plateSource),
                            s_PlateCode: vehicle.plateCode,
                            s_PlateNumber: vehicle.plateNumber,
                            s_Color: vehicle.color,
                            n_PlateLetter1: vehicle.plateLetter1
                                ? Number(vehicle.plateLetter1)
                                : 0,
                            n_PlateLetter2: vehicle.plateLetter2
                                ? Number(vehicle.plateLetter2)
                                : 0,
                            n_PlateLetter3: vehicle.plateLetter3
                                ? Number(vehicle.plateLetter3)
                                : 0,
                        };
                    }),
                };

                await AddUpdateWarehouseInOutPermit(body);

                toast.success(
                    !key
                        ? t("warehouseInOutPermitSubmittedSuccessfully")
                        : t("warehouseInOutPermitUpdatedSuccessfully")
                );

                setTimeout(() => {
                    navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
                }, 700);
            }
        },
        [
            AddUpdateWarehouseInOutPermit,
            addedDescriptionOfWarehouseItems,
            addedVehicles,
            addedWorkers,
            generalDescription,
            isGeneralDescriptionOther,
            key,
            navigate,
            setError,
            t,
        ]
    );

    const isApprovePermitPage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_WAREHOUSE_IN_OUT_PERMIT,
        [location.pathname]
    );

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);

    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);

    const isApproveDisabled = useMemo(
        () =>
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (warehousePermit?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== warehousePermit?.n_PermitNextApproverRoleID),
        [
            user?.n_RoleID,
            warehousePermit?.n_ApprovalStatus,
            warehousePermit?.n_PermitNextApproverRoleID,
        ]
    );

    const onApprove = useCallback(
        async (data: FormData) => {
            if (isApproveDisabled) return;

            if (new Date(data.permitEndDate) <= new Date()) {
                toast.error(t("permitExpiredCannotApprove"));
            } else if (
                new Date(data.permitEndDate) <= new Date(data.permitStartDate)
            ) {
                toast.error(t("permitDateError"));
            } else {
                setIsApprovePermitModalOpen(true);
            }
        },
        [isApproveDisabled, t]
    );

    const isRejectDisabled = useMemo(
        () =>
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            warehousePermit?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [warehousePermit?.n_ApprovalStatus]
    );

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        if (
            warehousePermit?.permitEndDate &&
            new Date(warehousePermit?.permitEndDate) <= new Date()
        ) {
            toast.error(t("permitExpiredCannotReject"));
            return;
        }

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, warehousePermit?.permitEndDate, t]);

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePermitPage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={
                                        isAddUpdateWarehouseInOutPermitLoading
                                    }
                                    disabled={
                                        isAddUpdateWarehouseInOutPermitLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}

                    {!key && (
                        <Button
                            isLoading={isAddUpdateWarehouseInOutPermitLoading}
                            disabled={isAddUpdateWarehouseInOutPermitLoading}
                            onClick={handleSubmit(onSubmit)}
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT}
                permitId={warehousePermit?.n_PermitWarehouseID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT}
                permitId={warehousePermit?.n_PermitWarehouseID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default ActionButtonGroup
